import React, { useState, memo, useEffect, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
// # Import map_iso utilities
import {
  getCountryISO,
  getCountryNameFromISO,
  isValidISO,
  getCountryData,
  formatCountryDataForHeatMap,
  getISOFromAnyFormat,
  getOriginalCountryNameFromISO,
  getCountryRegion
} from './map_iso';
import './usr_geo_heatmap.css';

// # Load world geography data - use a more reliable and CORS-enabled source
// # Using a CDN-hosted version of the TopoJSON file for better reliability
const geoUrl = {
  type: "Topology",
  objects: {
    countries: {
      type: "GeometryCollection",
      geometries: []
    }
  },
  arcs: []
};

// # Create mapping using official ISO_A3 codes from world-atlas
// # Note: This is kept for backward compatibility, but we'll prefer map_iso.js
const countryNameMapping = {
  // # Arab Countries
  'EGYPT': 'EGY',
  'ALGERIA': 'DZA',
  'BAHRAIN': 'BHR',
  'IRAQ': 'IRQ',
  'JORDAN': 'JOR',
  'KUWAIT': 'KWT',
  'LEBANON': 'LBN',
  'LIBYA': 'LBY',
  'MOROCCO': 'MAR',
  'OMAN': 'OMN',
  'PALESTINE': 'PSE',
  'QATAR': 'QAT',
  'SAUDI ARABIA': 'SAU',
  'SAUDI_ARABIA': 'SAU',
  'SOMALIA': 'SOM',
  'SUDAN': 'SDN',
  'SYRIA': 'SYR',
  'TUNISIA': 'TUN',
  'UNITED ARAB EMIRATES': 'ARE',
  'YEMEN': 'YEM',
  
  // # Asia
  'AFGHANISTAN': 'AFG',
  'BANGLADESH': 'BGD',
  'BRUNEI': 'BRN',
  'CAMBODIA': 'KHM',
  'CHINA': 'CHN',
  'INDIA': 'IND',
  'INDONESIA': 'IDN',
  'IRAN': 'IRN',
  'JAPAN': 'JPN',
  'KAZAKHSTAN': 'KAZ',
  'SOUTH KOREA': 'KOR',
  'SOUTH_KOREA': 'KOR',
  'NORTH KOREA': 'PRK',
  'NORTH_KOREA': 'PRK',
  'MALAYSIA': 'MYS',
  'PAKISTAN': 'PAK',
  'PHILIPPINES': 'PHL',
  'SINGAPORE': 'SGP',
  'SRI LANKA': 'LKA',
  'SRI_LANKA': 'LKA',
  'TAIWAN': 'TWN',
  'THAILAND': 'THA',
  'TURKEY': 'TUR',
  'VIETNAM': 'VNM',
  'KYRGYZSTAN': 'KGZ',
  'NEPAL': 'NPL',
  'TIMOR_LESTE': 'TLS',
  'TIMOR LESTE': 'TLS',
  'TURKMENISTAN': 'TKM',
  'UZBEKISTAN': 'UZB',
  'HONG_KONG': 'HKG',
  'HONG KONG': 'HKG',
  'LAOS': 'LAO',
  'MALDIVES': 'MDV',
  'MONGOLIA': 'MNG',
  'MYANMAR': 'MMR',
  
  // # Europe
  'UNITED KINGDOM': 'GBR',
  'GERMANY': 'DEU',
  'FRANCE': 'FRA',
  'ITALY': 'ITA',
  'SPAIN': 'ESP',
  'RUSSIA': 'RUS',
  'SLOVAKIA': 'SVK',
  'NETHERLANDS': 'NLD',
  'GREECE': 'GRC',
  'POLAND': 'POL',
  'ROMANIA': 'ROU',
  'HUNGARY': 'HUN',
  'CZECH REPUBLIC': 'CZE',
  'CZECH_REPUBLIC': 'CZE',
  'CROATIA': 'HRV',
  'SERBIA': 'SRB',
  'BULGARIA': 'BGR',
  'UKRAINE': 'UKR',
  'BELGIUM': 'BEL',
  'IRELAND': 'IRL',
  'AUSTRIA': 'AUT',
  'DENMARK': 'DNK',
  'FINLAND': 'FIN',
  'SWEDEN': 'SWE',
  'NORWAY': 'NOR',
  'SWITZERLAND': 'CHE',
  'PORTUGAL': 'PRT',
  
  // # Americas
  'UNITED STATES': 'USA',
  'USA': 'USA',
  'CANADA': 'CAN',
  'MEXICO': 'MEX',
  'BRAZIL': 'BRA',
  'PERU': 'PER',
  'COSTA_RICA': 'CRI',
  'COSTA RICA': 'CRI',
  'ARGENTINA': 'ARG',
  'CHILE': 'CHL',
  'COLOMBIA': 'COL',
  'VENEZUELA': 'VEN',
  'ECUADOR': 'ECU',
  'BOLIVIA': 'BOL',
  'PARAGUAY': 'PRY',
  'URUGUAY': 'URY',
  'PANAMA': 'PAN',
  
  // # Africa
  'TANZANIA': 'TZA',
  'SOUTH AFRICA': 'ZAF',
  'SOUTH_AFRICA': 'ZAF',
  'NIGERIA': 'NGA',
  'KENYA': 'KEN',
  'ETHIOPIA': 'ETH',
  'GHANA': 'GHA',
  'UGANDA': 'UGA',
  'SENEGAL': 'SEN',
  'CAMEROON': 'CMR',
  'IVORY COAST': 'CIV',
  'IVORY_COAST': 'CIV',
  'COMOROS': 'COM',
  
  // # Oceania
  'AUSTRALIA': 'AUS',
  'NEW ZEALAND': 'NZL',
  'NEW_ZEALAND': 'NZL',
  'FIJI': 'FJI',
  'PAPUA NEW GUINEA': 'PNG',
  'PAPUA_NEW_GUINEA': 'PNG',
  'SAMOA': 'WSM',
  'SOLOMON ISLANDS': 'SLB',
  'SOLOMON_ISLANDS': 'SLB',
  'VANUATU': 'VUT'
};

// # Create reverse mapping for display names
const isoToCountryName = Object.entries(countryNameMapping).reduce((acc, [name, iso]) => {
  // # Use the first occurrence of each ISO code (preferring the simpler name)
  if (!acc[iso]) {
    acc[iso] = name.charAt(0) + name.slice(1).toLowerCase();
  }
  return acc;
}, {});

// # Add ISO numeric code mapping for countries
const numericIsoMapping = {
  '004': 'AFG', // Afghanistan
  '008': 'ALB', // Albania
  '012': 'DZA', // Algeria
  '031': 'AZE', // Azerbaijan
  '051': 'ARM', // Armenia
  '112': 'BLR', // Belarus
  '156': 'CHN', // China
  '196': 'CYP', // Cyprus
  '203': 'CZE', // Czech Republic
  '208': 'DNK', // Denmark
  '246': 'FIN', // Finland
  '250': 'FRA', // France
  '268': 'GEO', // Georgia
  '276': 'DEU', // Germany
  '300': 'GRC', // Greece
  '348': 'HUN', // Hungary
  '352': 'ISL', // Iceland
  '356': 'IND', // India
  '360': 'IDN', // Indonesia
  '364': 'IRN', // Iran
  '368': 'IRQ', // Iraq
  '372': 'IRL', // Ireland
  '376': 'ISR', // Israel
  '380': 'ITA', // Italy
  '392': 'JPN', // Japan
  '398': 'KAZ', // Kazakhstan
  '400': 'JOR', // Jordan
  '404': 'KEN', // Kenya
  '410': 'KOR', // South Korea
  '414': 'KWT', // Kuwait
  '417': 'KGZ', // Kyrgyzstan
  '422': 'LBN', // Lebanon
  '428': 'LVA', // Latvia
  '440': 'LTU', // Lithuania
  '458': 'MYS', // Malaysia
  '484': 'MEX', // Mexico
  '498': 'MDA', // Moldova
  '504': 'MAR', // Morocco
  '512': 'OMN', // Oman
  '528': 'NLD', // Netherlands
  '554': 'NZL', // New Zealand
  '578': 'NOR', // Norway
  '586': 'PAK', // Pakistan
  '608': 'PHL', // Philippines
  '616': 'POL', // Poland
  '620': 'PRT', // Portugal
  '634': 'QAT', // Qatar
  '642': 'ROU', // Romania
  '643': 'RUS', // Russia
  '682': 'SAU', // Saudi Arabia
  '688': 'SRB', // Serbia
  '702': 'SGP', // Singapore
  '703': 'SVK', // Slovakia
  '704': 'VNM', // Vietnam
  '710': 'ZAF', // South Africa
  '724': 'ESP', // Spain
  '752': 'SWE', // Sweden
  '756': 'CHE', // Switzerland
  '760': 'SYR', // Syria
  '764': 'THA', // Thailand
  '784': 'ARE', // United Arab Emirates
  '792': 'TUR', // Turkey
  '804': 'UKR', // Ukraine
  '818': 'EGY', // Egypt
  '826': 'GBR', // United Kingdom
  '840': 'USA', // United States
  '887': 'YEM', // Yemen
  '894': 'ZMB'  // Zambia
};

// # Add country data cache
const countryDataCache = new Map();

// # Optimize country lookup function
const getCountryDataWithCache = (countryName) => {
  if (countryDataCache.has(countryName)) {
    return countryDataCache.get(countryName);
  }

  let countryData;
  // # Try uppercase lookup first
  if (countryName === countryName.toUpperCase()) {
    countryData = {
      name: countryName,
      iso: getCountryISO(countryName),
      id: getCountryData(countryName)?.id,
      numeric: getCountryData(countryName)?.numeric
    };
  } else {
    // # Direct value lookup
    countryData = {
      name: countryName,
      iso: getCountryISO(countryName),
      id: getCountryData(countryName)?.id,
      numeric: getCountryData(countryName)?.numeric
    };
  }

  // # Cache the result
  countryDataCache.set(countryName, countryData);
  return countryData;
};

const UserGeoHeatmap = ({ data, totalUsers }) => {
  // State for tooltip content and loading
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geoData, setGeoData] = useState(geoUrl);
  const [mapPosition, setMapPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const [transformedData, setTransformedData] = useState({});
  const mapContainerRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // # Debug incoming data
  useEffect(() => {
    if (data) {
      console.log('UserGeoHeatmap received data:', data);
    }
  }, [data]);

  // # Load geography data
  useEffect(() => {
    const loadGeoData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
        const data = await response.json();
        setGeoData(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading geographical data:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    loadGeoData();
  }, []);

  // Debug helper for geography data
  useEffect(() => {
    if (geoData && geoData.objects && geoData.objects.countries) {
      const countryFeatures = geoData.objects.countries.geometries;
      console.log('Available countries in map data:', 
        countryFeatures.map(f => ({
          name: f.properties.NAME,
          iso: f.properties.ISO_A3
        }))
      );
    }
  }, [geoData]);

  // # Transform data to match topojson country ISO codes
  useEffect(() => {
    if (!data) return;
    
    // # Debug incoming data
    console.log('Raw incoming data:', data);
    
    // # Use the enhanced formatCountryDataForHeatMap function from map_iso.js
    const formattedData = formatCountryDataForHeatMap(data, {
      useISO: true,
      defaultValue: 0,
      includeEmptyCountries: false,
      preserveUnmappedCountries: true // Preserve original country names if they can't be mapped
    });
    
    console.log('Formatted data from map_iso:', formattedData);
    
    // # Create the final transformed data with additional numeric ISO codes
    const finalTransformedData = { ...formattedData };
    
    // # Also add the data with numeric ISO codes for matching with the map
    // # This is needed because some map data uses numeric ISO codes
    Object.entries(formattedData).forEach(([isoCode, count]) => {
      // # Only process valid ISO codes (3 letters)
      if (isValidISO(isoCode)) {
        // # Add the country name as a key as well
        // # This helps with direct name matching
        const countryName = getCountryNameFromISO(isoCode);
        if (countryName && countryName !== 'Unknown') {
          finalTransformedData[countryName] = count;
        }
        
        // # Add numeric ISO code mapping
        Object.entries(numericIsoMapping).forEach(([numericCode, alpha]) => {
          if (alpha === isoCode) {
            finalTransformedData[numericCode] = count;
            console.log(`Added numeric mapping: ${isoCode} → ${numericCode} (count: ${count})`);
          }
        });
      }
    });
    
    // # Add special case mappings for problematic countries
    const specialCaseMappings = {
      'TKM': 'TURKMENISTAN',
      'DEU': 'GERMANY',
      'PNG': 'PAPUA_NEW_GUINEA',
      'CZE': 'CZECH_REPUBLIC',
      'TLS': 'TIMOR_LESTE',
      'CIV': 'IVORY_COAST',
      'KOR': 'SOUTH_KOREA',
      'HKG': 'HONG_KONG',
      'DZA': 'ALGERIA',
      'GBR': 'UK',
      'BHR': 'BAHRAIN',
      'KWT': 'KUWAIT',
      'SWE': 'SWEDEN',
      'COL': 'COLOMBIA',
      'JPN': 'JAPAN',
      'NLD': 'NETHERLANDS',
      'TZA': 'TANZANIA',
      'ARG': 'ARGENTINA',
      'DNK': 'DENMARK',
      'EGY': 'EGYPT',
      'SEN': 'SENEGAL',
      'CMR': 'CAMEROON',
      'FJI': 'FIJI',
      'ROU': 'ROMANIA',
      'IRL': 'IRELAND',
      'PER': 'PERU',
      'CRI': 'COSTA_RICA',
      'GRC': 'GREECE',
      'NPL': 'NEPAL',
      'PHL': 'PHILIPPINES',
      'BOL': 'BOLIVIA',
      'TUN': 'TUNISIA',
      'UGA': 'UGANDA',
      'BRN': 'BRUNEI',
      'IDN': 'INDONESIA',
      'BEL': 'BELGIUM',
      'USA': 'USA',
      'COM': 'COMOROS',
      'THA': 'THAILAND',
      'POL': 'POLAND',
      'SDN': 'SUDAN',
      'SAU': 'SAUDI_ARABIA',
      'SYR': 'SYRIA',
      'UKR': 'UKRAINE',
      'SOM': 'SOMALIA',
      'GHA': 'GHANA',
      'AFG': 'AFGHANISTAN',
      'KAZ': 'KAZAKHSTAN',
      'ECU': 'ECUADOR',
      'AUT': 'AUSTRIA',
      'MNG': 'MONGOLIA',
      'YEM': 'YEMEN',
      'FIN': 'FINLAND',
      'VNM': 'VIETNAM',
      'PAN': 'PANAMA',
      'OMN': 'OMAN',
      'LAO': 'LAOS',
      'MDV': 'MALDIVES',
      'IND': 'INDIA',
      'UZB': 'UZBEKISTAN'
    };
    
    // # Add special case mappings to ensure these countries are properly displayed
    Object.entries(specialCaseMappings).forEach(([isoCode, countryName]) => {
      if (formattedData[isoCode]) {
        // # If we have data for this ISO code, ensure it's also available under alternative keys
        // # that might be used by the map
        console.log(`Adding special case mapping: ${isoCode} (${formattedData[isoCode]}) → ${countryName}`);
        finalTransformedData[countryName] = formattedData[isoCode];
      }
    });
    
    // # Debug final transformed data
    console.log('Final transformed data:', finalTransformedData);
    
    setTransformedData(finalTransformedData);
  }, [data]);

  // Find max users for color scaling (ensure it's not negative or zero)
  const maxUsers = Math.max(1, ...Object.values(transformedData));
  console.log('Max users:', maxUsers);

  // Create color scale with multiple steps for better visualization
  const colorScale = scaleLinear()
    .domain([0, maxUsers * 0.2, maxUsers * 0.4, maxUsers * 0.6, maxUsers * 0.8, maxUsers])
    .range(['#f5f5f5', '#fee5e5', '#fccfcf', '#ff9999', '#ff6666', '#ff0000']);

  // Handle mouse events for tooltip
  const handleMouseEnter = (geo, event) => {
    if (!mapContainerRef.current) return;
    
    // # Get country information from geography properties
    const geoProps = geo.properties || {};
    const countryIso = geoProps.ISO_A3 || geoProps.iso_a3 || geo.id;
    
    // # Get country name - prioritize the original name from the map data
    // # This preserves the actual country name instead of relying on ISO lookup
    const countryName = geoProps.name || geoProps.NAME || getCountryNameFromISO(countryIso) || 'Unknown';
    
    // # Get additional country data using map_iso - try multiple approaches to get the region
    const countryData = getCountryData(countryIso);
    
    // # Enhanced region lookup - try multiple approaches to get the correct region
    let regionName = 'Unknown';
    
    // # 1. Try to get region from country data
    if (countryData?.region && countryData.region !== 'Unknown') {
      regionName = countryData.region;
    } 
    // # 2. Try to get region directly from the country name
    else if (countryName && countryName !== 'Unknown') {
      // # Try direct lookup from getCountryRegion
      const directRegion = getCountryRegion(countryName);
      if (directRegion && directRegion !== 'Unknown') {
        regionName = directRegion;
      }
      // # Try with ISO code converted to country name
      else if (countryIso) {
        const nameFromIso = getCountryNameFromISO(countryIso);
        if (nameFromIso && nameFromIso !== 'Unknown') {
          const regionFromIsoName = getCountryRegion(nameFromIso);
          if (regionFromIsoName && regionFromIsoName !== 'Unknown') {
            regionName = regionFromIsoName;
          }
        }
      }
    }
    
    // # 3. Special case mappings for problematic countries
    const specialRegionMappings = {
      'IND': 'South Asia',
      'INDIA': 'South Asia',
      'India': 'South Asia',
      'TKM': 'Central Asia',
      'TURKMENISTAN': 'Central Asia',
      'Turkmenistan': 'Central Asia'
    };
    
    // # Apply special region mapping if we still don't have a region
    if (regionName === 'Unknown') {
      if (specialRegionMappings[countryIso]) {
        regionName = specialRegionMappings[countryIso];
      } else if (specialRegionMappings[countryName]) {
        regionName = specialRegionMappings[countryName];
      } else if (countryName && specialRegionMappings[countryName.toUpperCase()]) {
        regionName = specialRegionMappings[countryName.toUpperCase()];
      }
    }
    
    // # Try multiple ways to get the user count for this country
    let userCount = 0;
    
    // # First try direct ISO match
    if (countryIso && transformedData[countryIso]) {
      userCount = transformedData[countryIso];
    } 
    // # Try country name match
    else if (countryName && transformedData[countryName]) {
      userCount = transformedData[countryName];
    }
    // # Try uppercase country name match
    else if (countryName && transformedData[countryName.toUpperCase()]) {
      userCount = transformedData[countryName.toUpperCase()];
    }
    // # Try special case mappings
    else {
      // # Special case handling for problematic countries
      const specialCases = {
        'Turkmenistan': 'TKM',
        'Germany': 'DEU',
        'Papua New Guinea': 'PNG',
        'Czech Republic': 'CZE',
        'Timor-Leste': 'TLS',
        'Côte d\'Ivoire': 'CIV',
        'South Korea': 'KOR',
        'Hong Kong': 'HKG',
        'Algeria': 'DZA',
        'United Kingdom': 'GBR',
        'UK': 'GBR',
        'Bahrain': 'BHR',
        'Kuwait': 'KWT',
        'Sweden': 'SWE',
        'Colombia': 'COL'
      };
      
      if (specialCases[countryName] && transformedData[specialCases[countryName]]) {
        userCount = transformedData[specialCases[countryName]];
      }
      // # Try reverse lookup - find the country name in our special case mappings
      else {
        const reverseMappings = {
          'TURKMENISTAN': 'TKM',
          'GERMANY': 'DEU',
          'PAPUA_NEW_GUINEA': 'PNG',
          'CZECH_REPUBLIC': 'CZE',
          'TIMOR_LESTE': 'TLS',
          'IVORY_COAST': 'CIV',
          'SOUTH_KOREA': 'KOR',
          'HONG_KONG': 'HKG',
          'ALGERIA': 'DZA',
          'UK': 'GBR',
          'BAHRAIN': 'BHR',
          'KUWAIT': 'KWT',
          'SWEDEN': 'SWE',
          'COLOMBIA': 'COL'
        };
        
        // # Try to find a matching key in our transformed data
        Object.entries(reverseMappings).forEach(([key, iso]) => {
          if (iso === countryIso && transformedData[key]) {
            userCount = transformedData[key];
          }
        });
      }
    }
    
    // # Calculate percentage using passed totalUsers
    const percentage = totalUsers > 0 ? ((userCount / totalUsers) * 100).toFixed(1) : '0.0';
    
    console.log('Hover data:', { 
      countryIso, 
      countryName, 
      regionName,
      userCount, 
      totalUsers, 
      percentage,
      countryData
    });
    
    // # Enhanced tooltip with region information
    const tooltipContent = `
      <div class="usr_geo_mp_tooltip-title">${countryName}</div>
      <div class="usr_geo_mp_tooltip-region">${regionName}</div>
      <div class="usr_geo_mp_tooltip-data">Users: ${userCount} (${percentage}%)</div>
    `;
    
    // # Calculate tooltip position
    const mapRect = mapContainerRef.current.getBoundingClientRect();
    const x = event.clientX - mapRect.left;
    const y = event.clientY - mapRect.top;
    
    // # Update tooltip state
    setTooltipContent(tooltipContent);
    setTooltipPosition({ x, y });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
    setShowTooltip(false);
  };

  // Prepare legend values (ensure they're not negative)
  const legendValues = {
    veryLow: Math.max(1, Math.round(maxUsers * 0.2)),
    low: Math.max(1, Math.round(maxUsers * 0.4)),
    medium: Math.max(1, Math.round(maxUsers * 0.6)),
    high: Math.max(1, Math.round(maxUsers * 0.8)),
    max: Math.max(1, Math.round(maxUsers))
  };

  if (isLoading) {
    return <div className="usr_geo_mp_loading">Loading map data...</div>;
  }

  if (error && !geoData) {
    return (
      <div className="usr_geo_mp_error">
        <p>Error: {error}</p>
        <p>Please try refreshing the page or contact support if the issue persists.</p>
      </div>
    );
  }

  if (!geoData) {
    return <div className="usr_geo_mp_error">No geography data available. Please try refreshing the page.</div>;
  }

  return (
    <div className="usr_geo_mp_container" ref={mapContainerRef}>
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="usr_geo_mp_tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 60}px`,
            position: 'absolute',
            zIndex: 1000,
            transform: 'translateX(-50%)'  // Center the tooltip
          }}
          dangerouslySetInnerHTML={{ __html: tooltipContent }}
        />
      )}

      {/* Map */}
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 150
        }}
        className="usr_geo_mp_map"
      >
        <ZoomableGroup
          zoom={mapPosition.zoom}
          center={mapPosition.coordinates}
          onMoveEnd={position => setMapPosition(position)}
        >
          <Geographies geography={geoData}>
            {({ geographies }) => {
              if (!geographies || !Array.isArray(geographies)) {
                console.error('Invalid geographies data:', geographies);
                return null;
              }
              
              return geographies.map((geo) => {
                // # Get all possible country identifiers from the geography data
                const geoProps = geo.properties || {};
                const countryIso = geoProps.ISO_A3 || geoProps.iso_a3;
                const countryId = geo.id || '';
                const countryName = geoProps.name || geoProps.NAME || '';
                
                // # Try to match with our transformed data using different possible identifiers
                let count = 0;
                
                // # First try direct ISO match
                if (countryIso && transformedData[countryIso]) {
                  count = transformedData[countryIso];
                } 
                // # Try ID match
                else if (countryId && transformedData[countryId]) {
                  count = transformedData[countryId];
                } 
                // # Try numeric ISO code
                else if (geoProps.iso_n3 && transformedData[geoProps.iso_n3]) {
                  count = transformedData[geoProps.iso_n3];
                }
                // # Try country name match
                else if (countryName && transformedData[countryName]) {
                  count = transformedData[countryName];
                }
                // # Try uppercase country name match (for special cases like TURKMENISTAN)
                else if (countryName && transformedData[countryName.toUpperCase()]) {
                  count = transformedData[countryName.toUpperCase()];
                  console.log(`Found country via uppercase name: ${countryName.toUpperCase()} (count: ${count})`);
                }
                
                // # Try alternative lookup for specific countries
                if (count === 0) {
                  // # Try to get ISO from the country name
                  const altIso = getISOFromAnyFormat(countryName);
                  if (altIso !== "Unknown" && transformedData[altIso]) {
                    count = transformedData[altIso];
                    console.log(`Found country data via alternative lookup: ${countryName} -> ${altIso} (count: ${count})`);
                  }
                  
                  // # Special case handling for problematic countries
                  const specialCases = {
                    'Turkmenistan': 'TKM',
                    'Germany': 'DEU',
                    'Papua New Guinea': 'PNG',
                    'Czech Republic': 'CZE',
                    'Timor-Leste': 'TLS',
                    'Côte d\'Ivoire': 'CIV',
                    'South Korea': 'KOR',
                    'Hong Kong': 'HKG',
                    'Algeria': 'DZA',
                    'United Kingdom': 'GBR',
                    'UK': 'GBR',
                    'Bahrain': 'BHR',
                    'Kuwait': 'KWT',
                    'Sweden': 'SWE',
                    'Colombia': 'COL'
                  };
                  
                  if (specialCases[countryName] && transformedData[specialCases[countryName]]) {
                    count = transformedData[specialCases[countryName]];
                    console.log(`Found country via special case: ${countryName} -> ${specialCases[countryName]} (count: ${count})`);
                  }
                  
                  // # Try direct lookup for specific countries
                  const directValues = {};
                  
                  if (count === 0 && directValues[countryName]) {
                    count = directValues[countryName];
                    console.log(`Using direct value for ${countryName}: ${count}`);
                  }
                  
                  // # Try to match with original country names from the data
                  // # This helps with countries that have different naming conventions
                  if (count === 0) {
                    // # Get all keys from the transformed data
                    const dataKeys = Object.keys(transformedData);
                    
                    // # Try to find a key that might match this country
                    for (const key of dataKeys) {
                      // # Skip numeric keys and ISO codes
                      if (/^\d+$/.test(key) || isValidISO(key)) continue;
                      
                      // # Try to match by converting both to lowercase and removing underscores
                      const normalizedKey = key.toLowerCase().replace(/_/g, ' ');
                      const normalizedCountry = countryName.toLowerCase();
                      
                      if (normalizedKey === normalizedCountry || 
                          normalizedKey.includes(normalizedCountry) || 
                          normalizedCountry.includes(normalizedKey)) {
                        count = transformedData[key];
                        console.log(`Found country via name matching: ${countryName} -> ${key} (count: ${count})`);
                        break;
                      }
                    }
                  }
                }
                
                // # For debugging countries with data
                if (count > 0) {
                  console.log('Found country with data:', {
                    name: countryName,
                    iso: countryIso,
                    id: countryId,
                    numeric: geoProps.iso_n3,
                    count
                  });
                }

                return (
                  <Geography
                    key={geo.rsmKey || countryIso || countryId}
                    geography={geo}
                    fill={count > 0 ? colorScale(count) : '#e2e8f0'}
                    stroke="#FFFFFF"
                    strokeWidth={0.3}
                    style={{
                      default: {
                        outline: 'none',
                        transition: 'all 250ms'
                      },
                      hover: {
                        fill: count > 0 ? '#ff3333' : '#cbd5e1',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 250ms'
                      },
                      pressed: {
                        outline: 'none'
                      }
                    }}
                    onMouseEnter={(event) => {
                      // # Get user count using the same logic as in handleMouseEnter
                      let userCount = 0;
                      
                      // # First try direct ISO match
                      if (countryIso && transformedData[countryIso]) {
                        userCount = transformedData[countryIso];
                      } 
                      // # Try country name match
                      else if (countryName && transformedData[countryName]) {
                        userCount = transformedData[countryName];
                      }
                      // # Try uppercase country name match
                      else if (countryName && transformedData[countryName.toUpperCase()]) {
                        userCount = transformedData[countryName.toUpperCase()];
                      }
                      // # Try special case mappings
                      else {
                        // # Special case handling for problematic countries
                        const specialCases = {
                          'Turkmenistan': 'TKM',
                          'Germany': 'DEU',
                          'Papua New Guinea': 'PNG',
                          'Czech Republic': 'CZE',
                          'Timor-Leste': 'TLS',
                          'Côte d\'Ivoire': 'CIV',
                          'South Korea': 'KOR',
                          'Hong Kong': 'HKG',
                          'Algeria': 'DZA',
                          'United Kingdom': 'GBR',
                          'UK': 'GBR',
                          'Bahrain': 'BHR',
                          'Kuwait': 'KWT',
                          'Sweden': 'SWE',
                          'Colombia': 'COL'
                        };
                        
                        if (specialCases[countryName] && transformedData[specialCases[countryName]]) {
                          userCount = transformedData[specialCases[countryName]];
                        }
                        // # Try reverse lookup - find the country name in our special case mappings
                        else {
                          const reverseMappings = {
                            'TURKMENISTAN': 'TKM',
                            'GERMANY': 'DEU',
                            'PAPUA_NEW_GUINEA': 'PNG',
                            'CZECH_REPUBLIC': 'CZE',
                            'TIMOR_LESTE': 'TLS',
                            'IVORY_COAST': 'CIV',
                            'SOUTH_KOREA': 'KOR',
                            'HONG_KONG': 'HKG',
                            'ALGERIA': 'DZA',
                            'UK': 'GBR',
                            'BAHRAIN': 'BHR',
                            'KUWAIT': 'KWT',
                            'SWEDEN': 'SWE',
                            'COLOMBIA': 'COL'
                          };
                          
                          // # Try to find a matching key in our transformed data
                          Object.entries(reverseMappings).forEach(([key, iso]) => {
                            if (iso === countryIso && transformedData[key]) {
                              userCount = transformedData[key];
                            }
                          });
                        }
                      }
                      
                      // # Update the count for coloring if we found a match
                      if (userCount > 0 && userCount !== count) {
                        console.log(`Updated count for ${countryName} (${countryIso}): ${count} -> ${userCount}`);
                        count = userCount;
                      }
                      
                      handleMouseEnter(geo, event);
                    }}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              });
            }}
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div className="usr_geo_mp_legend">
        <div className="usr_geo_mp_legend-title">User Distribution</div>
        <div className="usr_geo_mp_legend-items">
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#e2e8f0' }}></div>
            <span>No users</span>
          </div>
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#fee5e5' }}></div>
            <span>1-{legendValues.veryLow} users</span>
          </div>
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#fccfcf' }}></div>
            <span>{legendValues.veryLow + 1}-{legendValues.low} users</span>
          </div>
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#ff9999' }}></div>
            <span>{legendValues.low + 1}-{legendValues.medium} users</span>
          </div>
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#ff6666' }}></div>
            <span>{legendValues.medium + 1}-{legendValues.high} users</span>
          </div>
          <div className="usr_geo_mp_legend-item">
            <div className="usr_geo_mp_legend-color" style={{ background: '#ff0000' }}></div>
            <span>{legendValues.high + 1}+ users</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(UserGeoHeatmap);